import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { SubmitButton } from '../common/form/SubmitButton';
import { Input } from '../common/form/Input';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { DatePicker } from '../common/form/DatePicker';
import { questionnaireVersionListActions } from '../../store/entities/questionnaireVersionList';
import { QcsTextField } from '../common/basic/QcsTextField';
import { QuestionnaireLanguages } from './QuestionnaireLanguages';
import { isDateDisabled } from '../../utils/date';
import styled from '@emotion/styled';
import { getLangNameObj } from '../../utils/format';
import { useNavigation } from '../../utils/urlPaths';
import { getQuestionnaire, selectQuestionnaire, selectQuestionnaireState } from '../../store/entities/questionnaire';
import {
  getQuestionnaireVersion,
  questionnaireVersionActions,
  selectQuestionnaireVersion,
  selectQuestionnaireVersionState,
} from '../../store/entities/questionnaireVersion';
import { QuestionnaireVersionDto } from '@api/models/questionnaire/QuestionnaireVersionDto';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { TrainingRunButton } from '../trainingRun/TrainingRunButton';
import { ShowResourceContent } from '../ShowResourceContent';
import { questionnaireVersionService } from '@api/services/services';

const ButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

export const QuestionnaireVersionDetailData: FC = () => {
  const { questionnaireId, questionnaireVersionId } = useParams();
  const { t, i18n } = useTranslation();
  const { goBack, getBackPath } = useNavigation(`/questionnaire/${questionnaireId}`);
  const dispatch = useAppDispatch();
  const questionnaire = useAppSelector(selectQuestionnaire);
  const questionnaireVersion = useAppSelector(selectQuestionnaireVersion);

  useEffect(() => {
    if (questionnaireVersionId === 'new') {
      dispatch(questionnaireVersionActions.default());
    } else {
      dispatch(getQuestionnaireVersion(questionnaireVersionId!));
    }
    dispatch(getQuestionnaire(questionnaireId!));
  }, [questionnaireId, dispatch, questionnaireVersionId]);

  const handleSubmit = async (data: QuestionnaireVersionDto) => {
    if (questionnaireVersionId === 'new') {
      await questionnaireVersionService.create(data, { questionnaireId: questionnaireId! });
    } else {
      await questionnaireVersionService.update(questionnaireVersionId!, data);
    }

    dispatch(questionnaireVersionListActions.reload(true));
    goBack();
  };

  return (
    <>
      <DetailHeader title="questionnaire.version" backTo={getBackPath} />
      <ShowResourceContent fetchStateSelector={[selectQuestionnaireVersionState, selectQuestionnaireState]}>
        <FormikDebounce<QuestionnaireVersionDto>
          initialValues={questionnaireVersion}
          validationSchema={
            questionnaireVersion.editable
              ? Yup.object().shape({
                  validFrom: questionnaireVersion.first ? validations.dateRequired(t) : validations.stringDateNotPastOrTodayRequired(t),
                  successRate: validations.intRequiredMinMax(1, 100, t),
                })
              : undefined
          }
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <QcsTextField value={getLangNameObj(i18n, questionnaire)} label={t('questionnaire.detailTitle')} disabled />

              <DatePicker
                name="validFrom"
                label={t('questionnaire.validFrom')}
                disablePast
                disableHighlightToday
                shouldDisableDate={isDateDisabled}
                required
                disabled={!questionnaireVersion.editable || !!questionnaireVersion.first}
              />
              <DatePicker name="validTo" label={t('questionnaire.validTo')} disabled />

              <Input name="successRate" label={t('questionnaire.minSuccessRate')} type="number" disabled={!questionnaireVersion.editable} />

              <Input
                name="note"
                label={t('questionnaire.versionNote')}
                maxLength={250}
                multiline={true}
                rows={4}
                disabled={!questionnaireVersion.editable}
              />

              <ShowError resource={[resources.createQuestionnaireVersion, resources.updateQuestionnaireVersion]} />
              <ButtonsContainer>
                {questionnaireVersionId !== 'new' && (
                  <TrainingRunButton
                    questionnaireVersionId={questionnaireVersionId}
                    isPlayground
                    variant="outlined"
                    disabled={(questionnaireVersion.languages?.length ?? 0) < 1}
                  >
                    {t('questionnaire.runPlayground')}
                  </TrainingRunButton>
                )}
                <SubmitButton disabled={!questionnaireVersion.editable} />
              </ButtonsContainer>
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>

      {questionnaireVersionId !== 'new' && <QuestionnaireLanguages />}
    </>
  );
};